import { ThemeProvider } from "@mui/material/styles";
import _ from "lodash";
import React, { createRef, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { bindActionCreators } from "redux";
import * as providerActionCreators from "./actions";
import { log } from "../../Helper";
import * as spinnerStateActionCreator from "../../formControl/actions";
import { getProvidersOrders } from "./api";
import LoaderComponent from "../../LoaderComponent/LoaderComponent";
import { theme } from "../utility/constants";
import { MaterialTableLoadBuild } from "./loadBuildMaterialTable";
import LoadBuildPageHeader from "./loadBuildPageHeader";
import { LoadBuildDialog } from "./loadBuildDialog";
import LoadBuildForm from "../formControl/loadBuildForm";
import { useUserInfo } from "../../common/user-context";
import { useComarFilter } from "../../hooks/useComarFilter";
import FilterHeader from "../../components/FilterHeader";

const tableRef = createRef();
const Orders = () => {
  const { userData } = useUserInfo();

  const [pageSize] = useState(5000);
  const [pageNumber, setPageNumber] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState("");
  
  // Original data state
  const [originalTableData, setOriginalTableData] = useState([]);
  
  // NAVIGATION-BASED FILTERING: useComarFilter hook
  // This hook detects when user clicks on urgent action tiles (Missing Shipping Order, etc.)
  // and automatically filters the load-build data based on COMAR numbers from navigation state
  // - originalTableData: Complete dataset from API
  // - 'comarNo': Field name in load-build data containing COMAR numbers
  // Returns: filtered data and filter management functions
  const {
    isFiltered,      // Boolean: true when filter is active from urgent actions
    filterSource,    // String: source of filter (for debugging)
    filterTitle,     // String: display title like "LATE PICKUP" 
    totalFilteredOrders, // Number: count of filtered orders
    filteredData,    // Array: actual filtered dataset to display in table
    clearFilter      // Function: clears filter and shows all data
  } = useComarFilter(originalTableData, 'comarNo');
  const { setProvider, triggerLoadBuildAction } = bindActionCreators(
    providerActionCreators,
    useDispatch()
  );

  const [loadBuildValidatorState, setLoadBuildValidatorState] = useState(false);
  let loadBuildValidator = useSelector(
    (storeState) => storeState.loadBuildValidator
  );

  const { setSpinnerState } = bindActionCreators(
    spinnerStateActionCreator,
    useDispatch()
  );
  const [isLoading, setIsLoading] = useState(false);

  const [isLoadBuildDialogOpen, setIsLoadBuildDialogOpen] = useState(false);
  let spinnerState = useSelector((storeState) => storeState.spinnerState);

  // To handle dialog close/open on form submit button click
  useEffect(() => {
    if (loadBuildValidatorState && !spinnerState) {
      getTableData();
      setIsLoadBuildDialogOpen(false);
      setOpen(false);
      setSpinnerState(true);
    } else {
      if (isLoadBuildDialogOpen) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, [loadBuildValidatorState, spinnerState, isLoadBuildDialogOpen]);

  //handle modal operations
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (data) => {
    let errorFlag = false;
    for (let i = 0; i < data.length; i++) {
      if (!_.eq(data[0].from, data[i].from)) {
        toast.error(
          "Orders from multiple location are selected.  i.e. " +
            data[0].from +
            " and " +
            data[i].from
        );
        errorFlag = true;
        break;
      }
      if (!["PICKUP_READY", "AT_PICKUP", "DELIVERED", "DEPARTED"].includes(data[i].readyPickup)) {
        toast.error(
          "Error Occured, Serial " +
            data[i].serial +
            " is not ready to load the build"
        );
        errorFlag = true;
        break;
      }
      if (!_.eq(data[0].transportMode, data[i].transportMode)) {
        toast.error(
          "Orders with multiple transport modes are selected. i.e. " +
            data[0].transportMode +
            " and " +
            data[i].transportMode
        );
        errorFlag = true;
        break;
      }
    }

    if (!errorFlag) {
      setSelectedMachinesForBuild(data);
      console.log("Selected Machines for Load Build: ", data);
      setIsLoadBuildDialogOpen(true);
      setOpen(true);
    }
  };

  const handleClose = () => {
    triggerLoadBuildAction(false);
    setIsLoadBuildDialogOpen(false);
    setOpen(false);
  };

  const handleApply = useCallback(() => {
    triggerLoadBuildAction(true);
    setLoadBuildValidatorState(false);
  }, [loadBuildValidatorState]);

  const [previousForwarder, setPreviousForwarder] = React.useState();
  const [tableData, setTableData] = React.useState([]);
  const [locationList, setLocationList] = useState([]);
  const [imoList, setImoList] = useState([]);

  // FILTERED DATA SYNCHRONIZATION: 
  // Updates the table display data whenever the filtered dataset changes
  // This connects useComarFilter results to the MaterialTable component
  useEffect(() => {
    setTableData(filteredData); // filteredData comes from useComarFilter hook
  }, [filteredData]);

  useEffect(() => {
    setIsInternal(userData.internal);
    if (!_.isEqual(previousForwarder, supplierId_WithInternal)) {
      setPreviousForwarder(supplierId_WithInternal);
    }
    setLoadBuildValidatorState(loadBuildValidator);
  });

  const getTableData = async () => {
    setIsLoading(true);
    try {
      const supplierId = supplierId_WithInternal
        ? supplierId_WithInternal
        : supplierId_withForwarder;
      if (supplierId !== undefined || supplierId !== null) {
        const response = await getProvidersOrders(
          supplierId,
          pageSize,
          pageNumber
        );
        const responseData = response.pickupPage.content;
        // ORIGINAL DATA UPDATE: Store complete dataset for filtering
        // This feeds into useComarFilter hook as the source data to filter from
        setOriginalTableData(responseData);
        
        setLocationList(response.locationList);
        setImoList(response.imolist);
        setLastUpdatedTime(responseData);
      }
    } catch (error) {
      log("Error occured when fetching data: ", error);
      setTableData(Array(0));
    }
    setIsLoading(false);
  };

  const setLastUpdatedTime = (data) => {
    for (let i = 0; i < data.length; i++) {
      if (null !== data[i].convertedDate && "" !== data[i].convertedDate) {
        const date = data[0].convertedDate;
        setLastUpdateTime(date);
        break;
      }
    }
  };

  useEffect(() => {
    getTableData();
  }, [previousForwarder]);

  const supplierId_withForwarder = userData.supplierNumber;

  const supplierName = userData.descr;
  let supplierId_WithInternal = useSelector(
    (storeState) => storeState.setProvider
  );

  const [isInternal, setIsInternal] = useState(false);
  const [selectedMachinesForBuild, setSelectedMachinesForBuild] = useState([]);

  return (
    <div className="rounded-sm">
      {/* FILTER STATE INTEGRATION: Pass filter state to header component
          - isFiltered: Shows/hides FilterHeader component
          - filterTitle: Display name like "LATE PICKUP" 
          - totalFilteredOrders: Count for display
          - clearFilter: Function to clear urgent action filter
          - filterSource: For debugging/tracking filter origin */}
      <LoadBuildPageHeader
        isInternal={isInternal}
        setProvider={setProvider}
        supplierName={supplierName}
        lastUpdateTime={lastUpdateTime}
        getTableData={getTableData}
        isFiltered={isFiltered}
        filterTitle={filterTitle}
        totalFilteredOrders={totalFilteredOrders}
        onClearFilter={clearFilter}
        filterSource={filterSource}
      />
      <ThemeProvider theme={theme}>
        {isLoading && <LoaderComponent />}
        {!isLoading && (
          /* TABLE DATA DISPLAY: MaterialTable receives filtered data
             - tableData: Contains either all data or filtered data based on urgent actions
             - Data is automatically updated via useEffect when filteredData changes */
          <MaterialTableLoadBuild
            tableName="LoadBuild"
            tableRef={tableRef}
            pageSize={pageSize}
            tableData={tableData}
            setPageNumber={setPageNumber}
            handleClickOpen={handleClickOpen}
          />
        )}
        <LoadBuildDialog
          open={open}
          handleApply={handleApply}
          handleClose={handleClose}
          dialogContent={
            <LoadBuildForm
              selectedMachinesForBuild={selectedMachinesForBuild}
              locationList={locationList}
              imoList={imoList}
            />
          }
        />
      </ThemeProvider>
    </div>
  );
};

export default Orders;
